import React from 'react'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { useRouter } from 'expo-router'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { useAsyncMutation } from './useAsyncMutation'
import { openMyTable } from '../mttNavigation'
import { GracePeriodGate, LateJoinWindow, StartNowGate } from './LobbyCountdown'

type LobbyState = 'registration' | 'active' | 'finished' | 'cancelled' | 'expired' | string

type LobbyForActions = {
  _id: Id<'tournaments'>
  state: LobbyState
  registrationCount: number
  scheduledStartTime: number
}

interface MyTableForActions {
  gameId: string | null
  tournamentEliminated: boolean
  tableNumber?: number
}

interface StatusForActions {
  gracePeriodEndsAt: number
}

interface Props {
  lobby: LobbyForActions
  /** `null`: no seat and no elimination. `undefined`: still loading. */
  myTable: MyTableForActions | null | undefined
  status: StatusForActions | null
  /** Auth0 subject of the viewing user. */
  userId: string
  /** Display name. */
  userName: string
  /** Whether the user is registered for this tournament. */
  isRegistered: boolean
  /** Registered but waiting for a seat (all tables full). */
  isWaitlisted: boolean
  /** Whether the user is host or co-host. */
  isHostOrCohost: boolean
}

const START_NOW_WINDOW_MS = 300_000

interface WaitlistStatusProps {
  tournamentId: Id<'tournaments'>
  userId: string
  /** Undefined while status loads; the waitlist block shows meanwhile. */
  gracePeriodEndsAt: number | undefined
  isWaitlisted: boolean
  /** Registered, loaded, and holding neither a seat nor an elimination. */
  neverSeated: boolean
}

/**
 * The waitlist block, or once late registration closes, a notice for a
 * registrant who never got a seat. The server stops seating from the
 * waitlist when the grace period ends, so the two swap at that moment.
 */
function WaitlistStatus({ tournamentId, userId, gracePeriodEndsAt, isWaitlisted, neverSeated }: WaitlistStatusProps) {
  const { setEvent } = useStore().analytics
  const leaveWaitlistMutation = useMutation(api.tournaments.mutations.leaveTournamentWaitlist)
  const leaveWaitlist = useAsyncMutation(leaveWaitlistMutation)

  const waitlistBlock = isWaitlisted ? (
    <DS.Frame gap="4">
      <DS.Text variant="Caption/Regular" color="Brand/Dark Grey" style={{ textAlign: 'center' }}>
        All tables are full. You're on the waitlist and will be seated automatically if a spot opens before late
        registration closes.
      </DS.Text>
      <DS.Button
        type="On Dark"
        variant="Large/Secondary"
        disabled={leaveWaitlist.pending}
        onPress={() => {
          setEvent(events.mtt.leave_waitlist_tapped, { tournamentId })
          leaveWaitlist.run({ tournamentId, userId })
        }}
      >
        Leave Waitlist
      </DS.Button>
    </DS.Frame>
  ) : null
  const closedNotice = neverSeated ? (
    <DS.Text variant="Caption/Regular" color="Brand/Dark Grey" style={{ textAlign: 'center' }}>
      Late registration closed before a seat opened.
    </DS.Text>
  ) : null

  if (gracePeriodEndsAt === undefined) return waitlistBlock
  return <GracePeriodGate gracePeriodEndsAt={gracePeriodEndsAt} open={waitlistBlock} closed={closedNotice} />
}

/**
 * The action button stack rendered between the info card and the chat.
 *
 * Owns: register/unregister, host-only "Start Now",
 * eliminated user "Go to Table", grace-period "Join Table" late-join, and the
 * waitlist (closed with the grace period).
 *
 * Every mutation goes through `useAsyncMutation` so the buttons:
 *  - disable while pending (no double-submit)
 *  - toast on error (replaces the prior `alert()` calls that didn't render
 *    on native and left the user staring at a frozen UI)
 *
 * Time-dependent UI (the late-join countdown text + the start-window gate)
 * is delegated to self-ticking leaf components in `./LobbyCountdown` so
 * this component does NOT need to re-render every second.
 */
export const TournamentLobbyActions = React.memo(function TournamentLobbyActions({
  lobby,
  myTable,
  status,
  userId,
  userName,
  isRegistered,
  isWaitlisted,
  isHostOrCohost
}: Props) {
  const router = useRouter()
  const { setEvent } = useStore().analytics

  const registerMutation = useMutation(api.tournaments.mutations.registerForTournament)
  const unregisterMutation = useMutation(api.tournaments.mutations.unregisterFromTournament)
  const startTournamentMutation = useMutation(api.tournaments.mutations.startTournament)
  const joinLateMutation = useMutation(api.tournaments.mutations.joinTournamentLate)

  const register = useAsyncMutation(registerMutation)
  const unregister = useAsyncMutation(unregisterMutation)
  const startTournament = useAsyncMutation(startTournamentMutation)
  const joinLate = useAsyncMutation(joinLateMutation)

  const tournamentId = lobby._id
  const isPreStart = lobby.state === 'registration'
  const isActive = lobby.state === 'active'
  const hasTable = !!myTable?.gameId && !myTable.tournamentEliminated

  if (isPreStart) {
    return (
      <DS.Frame gap="8">
        {!isRegistered ? (
          <DS.Button
            type="On Light"
            variant="Large/Primary"
            backgroundColor="Brand/Maroon"
            disabled={register.pending}
            onPress={() => {
              setEvent(events.mtt.register_tapped, { tournamentId })
              register.run({ tournamentId, userId, userName })
            }}
          >
            Register
          </DS.Button>
        ) : (
          <DS.Button
            type="On Dark"
            variant="Large/Secondary"
            disabled={unregister.pending}
            onPress={() => {
              setEvent(events.mtt.unregister_tapped, { tournamentId })
              unregister.run({ tournamentId, userId })
            }}
          >
            Unregister
          </DS.Button>
        )}

        {isRegistered && (
          <DS.Text variant="Caption/Regular" color="Brand/Dark Grey" style={{ textAlign: 'center' }}>
            You'll be seated automatically when the tournament starts.
          </DS.Text>
        )}

        {isHostOrCohost && (
          <StartNowGate scheduledStartTime={lobby.scheduledStartTime} windowMs={START_NOW_WINDOW_MS}>
            <DS.Button
              type="On Light"
              variant="Medium/Primary"
              backgroundColor="Brand/Maroon"
              disabled={lobby.registrationCount < 1 || startTournament.pending}
              onPress={() => {
                setEvent(events.mtt.start_now_tapped, {
                  tournamentId,
                  registrationCount: lobby.registrationCount
                })
                startTournament.run({ tournamentId, userId })
              }}
            >
              Start Now
            </DS.Button>
          </StartNowGate>
        )}
      </DS.Frame>
    )
  }

  if (isActive) {
    const showLateJoin = !!status && !(myTable?.gameId || myTable?.tournamentEliminated || isWaitlisted)
    // Loaded with neither a seat nor an elimination: a late registrant who was
    // waitlisted or left the waitlist. `undefined` (still loading) must not
    // count, or seated players would flash the closed notice.
    const neverSeated = isRegistered && myTable === null
    return (
      <DS.Frame gap="8">
        {hasTable && (
          <DS.Button
            type="On Light"
            variant="Large/Primary"
            backgroundColor="Brand/Maroon"
            onPress={() => {
              setEvent(events.mtt.go_to_table_tapped, {
                tournamentId,
                gameId: myTable!.gameId,
                tableNumber: myTable!.tableNumber ?? null
              })
              openMyTable(router, myTable!.gameId!)
            }}
          >
            Go to Table
          </DS.Button>
        )}

        <WaitlistStatus
          tournamentId={tournamentId}
          userId={userId}
          gracePeriodEndsAt={status?.gracePeriodEndsAt}
          isWaitlisted={isWaitlisted}
          neverSeated={neverSeated}
        />

        {showLateJoin && (
          <LateJoinWindow gracePeriodEndsAt={status.gracePeriodEndsAt}>
            <DS.Button
              type="On Light"
              variant="Large/Primary"
              backgroundColor="Brand/Maroon"
              disabled={joinLate.pending}
              onPress={() => {
                setEvent(events.mtt.join_late_tapped, { tournamentId })
                // Unregistered callers are auto-registered server-side
                // (late registration); userName is needed for that path.
                joinLate.run({ tournamentId, userId, userName })
              }}
            >
              {isRegistered ? 'Join Table' : 'Join Tournament'}
            </DS.Button>
          </LateJoinWindow>
        )}
      </DS.Frame>
    )
  }

  // cancelled / expired / finished — no actions; the info card already explains state.
  return null
})
